import { useEffect, useState } from "react";
import useReveal from "../useReveal";
import { useSession } from "../session";
import { cachedGet, imageUrl, money, peekCache } from "../api";
import { CountUp, LevelBars, ScheduleChart } from "./Charts";
import HeroCanvas from "./HeroCanvas";
import {
  balanceNotes,
  faqs as sampleFaqs,
  incomeRules,
  referral,
  brandName,
  site,
  steps,
  tripReward,
  withdrawal as sampleWithdrawal,
} from "../content";

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

const icons = {
  package: "M4 8l8-4 8 4-8 4-8-4zm0 0v8l8 4m-8-12l8 4m0 8l8-4V8m-8 12v-8",
  schedule: "M12 7v5l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z",
  ledger: "M5 5h14M5 10h14M5 15h9M5 20h6",
  direct: "M16 19v-1a4 4 0 00-4-4H8a4 4 0 00-4 4v1m14-9l2 2 4-4M10 10a3 3 0 100-6 3 3 0 000 6z",
  levels: "M4 20h4v-6H4v6zm6 0h4V10h-4v10zm6 0h4V4h-4v16z",
  reward: "M12 15l-3.5 2 1-4L6 10l4-.4L12 6l2 3.6 4 .4-3.5 3 1 4L12 15z",
};

function Icon({ name }) {
  return (
    <span className="icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d={icons[name]} />
      </svg>
    </span>
  );
}

const ruleIcons = ["schedule", "direct", "levels", "reward"];

function HeroCard({ chartPackage, postedDay, postedAmount, totalScheduled, level, minimum }) {
  if (!chartPackage) return null;
  const posted = postedAmount ?? postedDay * chartPackage.dailyValue;
  const total = totalScheduled ?? chartPackage.durationDays * chartPackage.dailyValue;

  return (
    <div className="hero-visual">
      <div className="hero-art glass">
        <div className="hero-art-head">
          <div>
            <p className="eyebrow">{chartPackage.name}</p>
            <p className="hero-figure">
              {Number(posted).toFixed(2)}
              <span>
                {" "}
                / {Number(total).toFixed(2)} {chartPackage.currency}
              </span>
            </p>
            <p className="eyebrow muted">
              Day {postedDay} of {chartPackage.durationDays} · illustrative
            </p>
          </div>
        </div>
        <ScheduleChart pkg={chartPackage} postedDay={postedDay} height={220} />
        <div className="legend">
          <span>
            <i className="key-posted" /> Posted
          </span>
          <span>
            <i className="key-scheduled" /> Scheduled, not yet posted
          </span>
        </div>
      </div>

      <div className="float-chip chip-a glass" aria-hidden="true">
        <Icon name="levels" />
        <div>
          <strong>Level 1 · {level?.rate}</strong>
          <span>{level?.directs}</span>
        </div>
      </div>
      <div className="float-chip chip-b glass" aria-hidden="true">
        <Icon name="ledger" />
        <div>
          <strong>{minimum}</strong>
          <span>Minimum withdrawal</span>
        </div>
      </div>
    </div>
  );
}

function trackSpotlight(event) {
  const card = event.target.closest?.(".spot");
  if (!card) return;
  const rect = card.getBoundingClientRect();
  card.style.setProperty("--mx", `${event.clientX - rect.left}px`);
  card.style.setProperty("--my", `${event.clientY - rect.top}px`);
}

const LANDING_PATH = "/api/v1/public/landing";

export default function Landing() {
  const { token } = useSession();
  const loggedIn = Boolean(token);
  // Start from the last cached response so the page fills in instantly, then refresh.
  const [landing, setLanding] = useState(() => peekCache(LANDING_PATH)?.data || null);
  const [siteName, setSiteName] = useState("");
  const [notes, setNotes] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  useReveal();

  useEffect(() => {
    cachedGet(LANDING_PATH)
      .then((payload) => setLanding(payload.data || null))
      .catch(() => {});
    cachedGet("/api/v1/public/site")
      .then((payload) => setSiteName(payload.data?.siteName || ""))
      .catch(() => {});
    cachedGet("/api/v1/public/announcements")
      .then((payload) => setNotes(Array.isArray(payload.data) ? payload.data : []))
      .catch(() => setNotes([]));
    cachedGet("/api/v1/public/packages")
      .then((payload) => setCatalog(Array.isArray(payload.data) ? payload.data : []))
      .catch(() => setCatalog([]));
  }, []);

  const plans = landing?.packages || [];
  const planKey = (item) => item?.id || item?.name || "";
  const selected = plans.find((item) => planKey(item) === selectedId) || plans[0];
  useEffect(() => {
    const list = landing?.packages || [];
    if (list.length && !list.some((item) => planKey(item) === selectedId)) setSelectedId(planKey(list[0]));
  }, [landing, selectedId]);

  const chartPackage = landing?.chart
    ? {
        id: landing.chart.name,
        name: landing.chart.name,
        currency: landing.currency,
        durationDays: landing.chart.durationDays,
        dailyValue: landing.chart.dailyAmount,
      }
    : null;
  const postedDay = landing?.chart?.postedDay ?? null;
  const levelRows = landing?.levels?.length ? landing.levels : referral.levels;
  const levelSchedule = landing?.schedule || referral.schedule;
  const minimumLabel = landing ? `${landing.minimumWithdrawal} ${landing.currency}` : sampleWithdrawal.minimum;
  const withdrawalRules = landing?.withdrawal || {
    minimum: sampleWithdrawal.minimum,
    fee: sampleWithdrawal.fee,
    methods: sampleWithdrawal.methods,
  };
  const faqRows = landing?.faqs?.length ? landing.faqs : sampleFaqs;
  const rewards = landing?.rewards || [];
  const featuredReward = rewards[0] || null;
  // Admin-published reward when there is one; otherwise the investment-milestone trip from content.js.
  const amountLabel = `${tripReward.threshold.toLocaleString("en-US")} ${tripReward.currency}`;
  const trip = featuredReward
    ? {
        title: featuredReward.title,
        gold: `${featuredReward.requiredDirects} qualified directs`,
        rest: "unlock",
        chip: `${featuredReward.requiredDirects} qualified directs unlock ${featuredReward.title}`,
        steps: [
          { title: "Qualify", text: `Reach ${featuredReward.requiredDirects} qualified direct referrals.` },
          { title: "Claim", text: featuredReward.description },
        ],
        terms: "This reward is not wallet cash. Claim it from the rewards page after you qualify.",
        qualifies: `${featuredReward.requiredDirects} directs`,
        stub: `${featuredReward.requiredDirects}+`,
      }
    : {
        title: tripReward.title,
        gold: amountLabel,
        rest: "or more and earn an",
        chip: `Invest ${amountLabel}+ and earn an international trip`,
        steps: tripReward.steps,
        terms: tripReward.terms,
        qualifies: `${amountLabel}+`,
        stub: `${tripReward.threshold.toLocaleString("en-US")}+`,
      };
  const directPercent = landing?.directCommissionPercent;
  const rules = incomeRules.map((rule) => {
    if (rule.title === "Direct referral" && directPercent != null) {
      return {
        ...rule,
        text: `A sponsor receives ${directPercent}% of a referral’s first package purchase, once. Later purchases by the same member do not pay it again.`,
      };
    }
    return rule;
  });
  const stats = landing?.stats?.length ? landing.stats : [];

  return (
    <main id="home" onPointerMove={trackSpotlight}>
      <section className="hero">
        <div className="aurora" aria-hidden="true">
          <span />
          <span />
        </div>
        <HeroCanvas />
        <div className="wrap hero-grid">
          <div className="hero-copy">
            <p className="kicker kicker-pill">{brandName(siteName)}</p>
            <h1>
              Fund an account, activate a package, and track income that has{" "}
              <em className="serif-accent">actually posted.</em>
            </h1>
            <p className="lede">
              {site.name} is the public site for a package-based program. Rules, prices and payouts come from the admin
              configuration that is currently published. This page does not promise a return and it does not show
              trading results.
            </p>
            <div className="row">
              <a className="btn btn-primary" href={token ? "#/dashboard" : "#/signup"}>
                {token ? "Dashboard" : "Create Account"}
                <span className="arrow" aria-hidden="true">
                  →
                </span>
              </a>
              <a className="btn btn-secondary" href={token ? "#/packages/buy" : "#trip"}>
                {token ? "View Packages" : "Travel reward"}
              </a>
            </div>
            <a className="trip-chip" href="#trip">
              <span aria-hidden="true">✈</span>
              {trip.chip}
              <em aria-hidden="true">→</em>
            </a>
          </div>
          <HeroCard
            chartPackage={chartPackage}
            postedDay={postedDay}
            postedAmount={landing?.chart?.postedAmount}
            totalScheduled={landing?.chart?.totalScheduled}
            level={levelRows[0]}
            minimum={minimumLabel}
          />
        </div>

        <div className="wrap">
          <dl className="stats glass">
            {stats.map((stat) => (
              <div key={stat.label}>
                <dt>{stat.label}</dt>
                <dd>
                  <CountUp value={stat.value} />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {notes.length > 0 && (
        <section className="section" id="announcements">
          <div className="wrap">
            <div className="section-head reveal">
              <p className="kicker">Announcements</p>
              <h2>Published updates.</h2>
            </div>
            <div className="cards">
              {notes.map((note) => (
                <article className="card spot reveal" key={note.id}>
                  <h3>{note.title}</h3>
                  <p>{note.body}</p>
                  {note.createdAt && <p className="fine">{new Date(note.createdAt).toLocaleDateString()}</p>}
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="packages" className="section">
        <div className="wrap">
          <div className="section-head reveal">
            <p className="kicker">Packages</p>
            <h2>Active packages you can open.</h2>
          </div>
          {catalog === null ? null : catalog.length ? (
            <div className="cards">
              {catalog.map((item) => (
                <article className="card package-card spot reveal" key={item.id}>
                  {item.image && <img className="package-photo" src={imageUrl(item.image)} alt="" />}
                  <p className="badge">{item.code}</p>
                  <h3>{item.name || "package"}</h3>
                  <p className="price">
                    {money(item.minAmount)}
                    {item.maxAmount !== item.minAmount && <span> – {money(item.maxAmount)}</span>}
                    <span> {landing?.currency || ""}</span>
                  </p>
                  <dl>
                    <div>
                      <dt>Days</dt>
                      <dd>{item.durationDays}</dd>
                    </div>
                    <div>
                      <dt>Return</dt>
                      <dd>{item.multiplier}x</dd>
                    </div>
                  </dl>
                  <a className="btn btn-primary" href={`#/packages/${item.id}`}>
                    View package
                  </a>
                </article>
              ))}
            </div>
          ) : (
            <p className="fine">No active packages are published yet.</p>
          )}
        </div>
      </section>

      <section id="about" className="section">
        <div className="wrap narrow reveal center">
          <p className="kicker">About</p>
          <h2>A member site for published packages.</h2>
          <p className="muted-lg">
            Members register, fund an account, purchase a package, and then follow scheduled income, referrals,
            transfers and withdrawals. Company registration, licences and history are shown only after an approved
            profile is published. This page does not display testimonials or a live member count.
          </p>
        </div>
      </section>

      <section id="how-it-works" className="section">
        <div className="wrap">
          <div className="section-head reveal">
            <p className="kicker">How it works</p>
            <h2>Five steps from registration to a withdrawal request.</h2>
          </div>
          <ol className="steps reveal">
            {steps.map((step, index) => (
              <li key={step.title} className="spot" style={{ "--d": `${index * 90}ms` }}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
          <ul className="notes reveal">
            {balanceNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="trip" className="section trip-section">
        <div className="wrap">
          <div className="trip reveal">
            <div className="trip-sky" aria-hidden="true">
              <svg viewBox="0 0 600 220" preserveAspectRatio="none">
                <path id="trip-route" d="M20 190 C 160 40, 420 40, 580 150" />
              </svg>
              <span className="trip-plane">
                <svg viewBox="0 0 24 24">
                  <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 00-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" />
                </svg>
              </span>
            </div>

            <div className="trip-copy">
              <p className="kicker kicker-pill">Exclusive reward</p>
              <h2>
                {!featuredReward && "Invest "}
                <span className="gold-text">{trip.gold}</span> {trip.rest}{" "}
                <em className="serif-accent">{trip.title.toLowerCase()}.</em>
              </h2>
              <ol className="trip-steps">
                {trip.steps.map((step, index) => (
                  <li key={step.title} style={{ "--d": `${index * 100}ms` }}>
                    <span>{index + 1}</span>
                    <div>
                      <strong>{step.title}</strong>
                      <small>{step.text}</small>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="row">
                <a className="btn btn-primary" href={loggedIn ? "#/packages/buy" : "#/signup"}>
                  {loggedIn ? "Buy a package" : "Create account"}
                  <span className="arrow" aria-hidden="true">
                    →
                  </span>
                </a>
                <a className="btn btn-secondary" href={loggedIn ? "#/rewards" : "#rewards"}>
                  Reward details
                </a>
              </div>
              <p className="trip-terms">{trip.terms}</p>
            </div>

            <div className="pass" aria-label={`Reward: ${trip.title}, qualifies at ${trip.qualifies}`}>
              <div className="pass-main">
                <div className="pass-top">
                  <span className="pass-brand">{site.name.toUpperCase()} · REWARD PASS</span>
                  <span className="pass-class">Elite</span>
                </div>
                <div className="pass-route">
                  <div>
                    <small>From</small>
                    <strong>YOU</strong>
                    <span>Your city</span>
                  </div>
                  <div className="pass-line" aria-hidden="true">
                    <i />
                    <svg viewBox="0 0 24 24">
                      <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 00-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" />
                    </svg>
                  </div>
                  <div className="to">
                    <small>To</small>
                    <strong>INTL</strong>
                    <span>{trip.title}</span>
                  </div>
                </div>
                <dl className="pass-meta">
                  <div>
                    <dt>Qualifies at</dt>
                    <dd>{trip.qualifies}</dd>
                  </div>
                  <div>
                    <dt>Benefit</dt>
                    <dd>Trip, not cash</dd>
                  </div>
                  <div>
                    <dt>Claim</dt>
                    <dd>Rewards page</dd>
                  </div>
                </dl>
              </div>
              <div className="pass-stub" aria-hidden="true">
                <span className="pass-barcode" />
                <small>{trip.stub}</small>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="income" className="section">
        <div className="wrap">
          <div className="section-head reveal">
            <p className="kicker">Income</p>
            <h2>What the active rules can pay.</h2>
          </div>
          <div className="cards cards-4">
            {rules.map((rule, index) => (
              <article className="card spot reveal" key={rule.title} style={{ "--d": `${index * 80}ms` }}>
                <Icon name={ruleIcons[index] || "ledger"} />
                <h3>{rule.title}</h3>
                <p>{rule.text}</p>
              </article>
            ))}
          </div>

          {plans.length ? <div id="calculator" className="calculator glass reveal">
            <div className="calc-side">
              <p className="badge">Illustrative calculator</p>
              <h3>Example for the selected package version.</h3>
              <p className="fine">
                This restates the published example. It does not credit a balance, and it is not a forecast.
              </p>
              <label htmlFor="package-version">Package version</label>
              <select
                id="package-version"
                value={planKey(selected)}
                onChange={(event) => setSelectedId(event.target.value)}
              >
                {plans.map((item) => (
                  <option key={planKey(item)} value={planKey(item)}>
                    {item.name}
                  </option>
                ))}
              </select>
              {selected && (
                <dl>
                  <Field label="Price" value={[selected.priceLabel, landing?.currency].filter(Boolean).join(" ")} />
                  <Field label="Payout method" value={selected.payoutMethod} />
                  <Field label="Daily amount" value={selected.dailyAmount} />
                  <Field label="Formula" value={selected.formula} />
                  <Field label="Duration" value={selected.duration} />
                  <Field label="Total scheduled" value={selected.totalScheduled} />
                  {!selected.totalScheduled && (
                    <div>
                      <dt>Total scheduled</dt>
                      <dd>Not fixed for this version.</dd>
                    </div>
                  )}
                  <Field label="Principal" value={selected.principal} />
                </dl>
              )}
            </div>
            {selected && (
              <div className="calc-chart">
                <p className="eyebrow">Cumulative scheduled payouts · {landing?.currency || ""}</p>
                <ScheduleChart key={planKey(selected)} pkg={{ ...selected, currency: landing?.currency }} height={300} />
                <p className="fine">Hover the chart to read any day of the published schedule.</p>
              </div>
            )}
          </div> : null}
        </div>
      </section>

      <section id="referrals" className="section band">
        <div className="wrap referral-grid">
          <div className="reveal">
            <p className="kicker">Referrals</p>
            <h2>
              {levelRows.length === 5
                ? "Five-level example from the current configuration."
                : "Referral levels from the current configuration."}
            </h2>
            <p className="muted-lg">
              Your own qualified direct referrals unlock how deep you can earn. Calculation base:{" "}
              {referral.calculationBase}. Schedule: {levelSchedule}.
            </p>
          </div>
          <div className="card chart-card reveal" style={{ "--d": "100ms" }}>
            <p className="eyebrow">Commission rate by level</p>
            <LevelBars levels={levelRows} />
          </div>
        </div>
        <div className="wrap">
          <div className="table-wrap reveal">
            <table>
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Rate</th>
                  <th>Qualified directs</th>
                  <th>Schedule</th>
                </tr>
              </thead>
              <tbody>
                {levelRows.map((level) => (
                  <tr key={level.level}>
                    <td>
                      <span className="level-chip">L{level.level}</span>
                    </td>
                    <td className="rate">{level.rate}</td>
                    <td>
                      <span className="dots" aria-hidden="true">
                        {levelRows.map((_, i) => (
                          <i key={i} className={i < level.level ? "on" : ""} />
                        ))}
                      </span>
                      {level.directs}
                    </td>
                    <td>{levelSchedule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {rewards.length ? (
        <section id="rewards" className="section">
          <div className="wrap">
            <div className="section-head reveal">
              <p className="kicker">Rewards</p>
              <h2>Published rewards.</h2>
            </div>
            <div className="cards">
              {rewards.map((item) => (
                <article className="card spot card-accent reveal" key={item.id}>
                  <Icon name="reward" />
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <p className="fine">Needs {item.requiredDirects} qualified directs. This is not added to the wallet.</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section id="withdrawals" className="section band">
        <div className="wrap">
          <div className="section-head reveal">
            <p className="kicker">Transfers and withdrawals</p>
            <h2>Current withdrawal conditions.</h2>
          </div>
          <dl className="plain-list reveal">
            <Field label="Minimum" value={withdrawalRules.minimum} />
            <Field label="Maximum" value={withdrawalRules.maximum} />
            <Field label="Fees" value={withdrawalRules.fee} />
            <Field label="Methods" value={withdrawalRules.methods} />
          </dl>
          <p className="callout reveal">
            Transfers use available balance, show the receiver and the fee, and need a confirmation tied to those
            details. A transfer does not qualify a referral.
          </p>
        </div>
      </section>

      <section id="faq" className="section">
        <div className="wrap narrow reveal">
          <div className="center">
            <p className="kicker">FAQs</p>
            <h2>A short preview of published questions.</h2>
          </div>
          <div className="faq">
            {faqRows.map((item) => (
              <details key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="section">
        <div className="wrap">
          <div className="contact-panel glass reveal">
            <div>
              <p className="kicker">Support</p>
              <h2>Contact and support hours.</h2>
              <p className="muted-lg">
                {landing?.supportEmail
                  ? <>Write to <a href={`mailto:${landing.supportEmail}`}>{landing.supportEmail}</a>.</>
                  : "Support email has not been published yet. Use the questions above."}
              </p>
              <p className="hours">{site.support.hours}</p>
              <p className="fine">{site.support.hoursNote}</p>
              <a className="btn btn-primary" href="#faq">
                Read the FAQs
              </a>
            </div>
            <article id="account" className="card card-accent">
              <h3>{token ? "Your account" : "Login and sign up"}</h3>
              <p>
                {token
                  ? "You are signed in. Open the dashboard to see your balance, packages and team."
                  : "Registration asks for your name, email, mobile, country, password and referral code, plus acceptance of the current Terms and Risk Disclosure."}
              </p>
              <div className="row">
                {token ? (
                  <a className="btn btn-primary" href="#/dashboard">
                    Dashboard
                  </a>
                ) : (
                  <>
                    <a className="btn btn-primary" href="#/signup">
                      Create account
                    </a>
                    <a className="btn btn-ghost" href="#/login">
                      Log in
                    </a>
                  </>
                )}
              </div>
            </article>
          </div>
        </div>
      </section>
    </main>
  );
}
